import React, { useState } from 'react';
import type { View } from '../../types';
import { PlusIcon } from '../icons';
import { Tab, Button, ActionDropdown, DataTable, FilterModal, type TabItem, type DropdownAction, type FilterGroup } from '../ui';
import { usePublications, usePublicationStats, useDeletePublication } from '../../lib/api/hooks/publication';
import { showToast } from '../../lib/toast';
import type { Publication, PublicationFilterParams } from '../../lib/api/types';

interface PublicationsProps {
  setView: (view: View) => void;
}

const Publications: React.FC<PublicationsProps> = ({ setView }) => {
  const [activeTab, setActiveTab] = useState<'published' | 'drafts'>('published');
  const [searchValue, setSearchValue] = useState('');
  const [page] = useState(1);
  const [pageSize] = useState(50);
  const [activeFilters, setActiveFilters] = useState<PublicationFilterParams>({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);
 

  // Filter groups configuration
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([
    {
      title: 'Publication Category',
      key: 'category',
      type: 'checkbox',
      options: [
        { label: 'Journal', value: 'JOURNAL', checked: false },
        { label: 'Law', value: 'LAW', checked: false },
        { label: 'Policy', value: 'POLICY', checked: false },
        { label: 'Report', value: 'REPORT', checked: false }
      ]
    },
    {
      title: 'Type',
      key: 'type',
      type: 'checkbox',
      options: [
        { label: 'Legal Foundation', value: 'legalFoundation', checked: false }
      ]
    }
  ]);

  // Fetch stats
  const { data: statsData } = usePublicationStats();
  const stats = statsData || { publishedPublications: 0, draftPublications: 0, totalPublications: 0 };

  // Fetch publications
  const { data: publicationsData, isLoading, error } = usePublications(
    { page, size: pageSize },
    {
      status: activeTab === 'published' ? 'PUBLISH' : 'DRAFT',
      ...activeFilters
    }
  );

  const { mutate: deletePublication, isPending: isDeleting } = useDeletePublication();

  const publications = publicationsData?.data?.data || [];
  const publishedCount = stats.publishedPublications;
  const draftsCount = stats.draftPublications;

  const tabItems: TabItem<'published' | 'drafts'>[] = [
    { key: 'published', label: 'Published', count: publishedCount },
    { key: 'drafts', label: 'Drafts', count: draftsCount }
  ];

  // Handle filter option toggle
  const handleFilterChange = (groupKey: string, optionValue: string | boolean) => {
    setFilterGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.key === groupKey
          ? {
              ...group,
              options: group.options.map((opt) =>
                opt.value === optionValue
                  ? { ...opt, checked: group.type === 'radio' ? true : !opt.checked }
                  : group.type === 'radio'
                  ? { ...opt, checked: false }
                  : opt
              )
            }
          : group
      )
    );
  };

  // Apply filters
  const handleApplyFilters = (filters: Record<string, unknown>) => {
    const apiFilters: PublicationFilterParams = {};

    // Map filter keys to API parameters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'category') {
        // Handle category selection - take first selected value
        if (Array.isArray(value) && value.length > 0) {
          apiFilters.publicationCategory = value[0] as string;
        } else if (typeof value === 'string') {
          apiFilters.publicationCategory = value;
        }
      }
    });

    setActiveFilters(apiFilters);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilterGroups((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        options: group.options.map((opt) => ({ ...opt, checked: false }))
      }))
    );
    setActiveFilters({});
  };

  const handleViewDetails = (publicationId: string) => {
    setView({ page: 'Publications', subPage: 'detail', id: publicationId });
  };

  const handleEdit = (publicationId: string) => {
    setView({ page: 'Publications', subPage: 'edit', id: publicationId });
  };

  const handleDelete = (publicationId: string) => {
    deletePublication(publicationId, {
      onSuccess: (response) => {
        if (response.success) {
          showToast.success('Publication deleted successfully!');
        }
      },
      onError: (error: Error) => {
        showToast.error('Failed to delete: ' + (error.message || 'Unknown error'));
      }
    });
  };

  const handleDownload = (publicationUrl: string, title: string) => {
    // Open publication URL in a new tab
    window.open(publicationUrl, '_blank');
    showToast.success(`Downloading ${title}...`);
  };

  const getDropdownActions = (publication: Publication): DropdownAction[] => [
    {
      label: 'View Details',
      onClick: (e) => {
        e.stopPropagation();
        handleViewDetails(publication.publicationId);
      }
    },
    {
      label: 'Edit',
      onClick: (e) => {
        e.stopPropagation();
        handleEdit(publication.publicationId);
      }
    },
    {
      label: 'Download',
      onClick: (e) => {
        e.stopPropagation();
        handleDownload(publication.publicationUrl, publication.title);
      }
    },
    {
      label: 'Delete',
      onClick: (e) => {
        e.stopPropagation();
        handleDelete(publication.publicationId);
      },
      variant: 'danger' as const,
      disabled: isDeleting
    }
  ];

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        filterGroups={filterGroups}
        onChange={handleFilterChange}
      />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Tab
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => setView({ page: 'Publications', subPage: 'new' })}
          >
            Upload New
          </Button>
        </div>

        <DataTable
          data={publications}
          columns={[]} // We're using custom renderRow
          searchable
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          filterable
          onFilterClick={() => setFilterModalOpen(true)}
          loading={isLoading}
          loadingMessage="Loading publications..."
          error={error}
          errorTitle="Error Loading Publications"
          emptyTitle={activeTab === 'published' ? 'No Publication' : 'No Draft'}
          emptyMessage={activeTab === 'published' ? 'You are yet to post a publication' : 'Your draft is empty'}
          emptyAction={
            activeTab === 'published' ? (
              <Button
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => setView({ page: 'Publications', subPage: 'new' })}
              >
                Upload New
              </Button>
            ) : undefined
          }
          renderRow={(pub: Publication) => {
            const handleRowClick = () => {
              if (isDeleting) return; // Prevent clicks during deletion
              setView({ page: 'Publications', subPage: activeTab === 'published' ? 'detail' : 'edit', id: pub.publicationId });
            };

            return (
            <div
              key={pub.publicationId}
              className={`flex items-center gap-4 p-4 ${!isDeleting ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed'} rounded-lg border border-gray-200 ${isDeleting ? 'opacity-50' : ''}`}
              onClick={handleRowClick}
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 text-sm truncate">{pub.title}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                  <span>{pub.publicationCategory}</span>
                  <span className="text-gray-300">|</span>
                  <span>{formatDate(pub.createdAt)}</span>
                  <span className="text-gray-300">|</span>
                  <span>{pub.createdByName}</span>
                  {pub.legalFoundation && (
                    <span className="ml-2 text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      Legal Foundation
                    </span>
                  )}
                </div>
              </div>
              <div className="hidden md:block text-center px-4">
                <p className="text-xs text-gray-500">Total View</p>
                <p className="text-sm font-semibold text-gray-800">{pub.totalViews || 600}</p>
              </div>
              <div className="hidden md:block text-center px-4">
                <p className="text-xs text-gray-500">Total Downloads</p>
                <p className="text-sm font-semibold text-gray-800">{pub.totalDownloads || 600}</p>
              </div>
              <ActionDropdown
                id={pub.publicationId}
                actions={getDropdownActions(pub)}
              />
            </div>
            );
          }}
        />
      </div>
    </>
  );
};

export default Publications;
